import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { UILocation } from "@flights/types";
import { State as SearchState } from "../store/search/state";
// eslint-disable-next-line import/no-deprecated
import { useStore } from "../store";
import { getSearchApiMarketingParams, isOfMetaOrigin } from "../utils/marketing-url-params";
import {
  LOCATION_IATA_CODE_AND_TYPE_SEPARATOR,
  MULTISTOP_IATA_LIST_SEPARATOR,
  PARAM_LIST_SEPARATOR
} from "@flights/constants";
import { flightsSessionStore, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { trackWithDefaultStage } from "@flights/et-universal";
import useUserAgent from "./useUserAgent";

function getLocationCodes(locations: UILocation[]): string {
  return locations
    .map(({ code, type }) => {
      if (isFeatureRunningClientSide("FLIGHTS_WEB_CAT_PNH_OVERRIDE")) {
        // ? Override PNH to KTI
        // ? Why - https://docs.google.com/document/d/14TwyYViAm_K61TY0R9XEwSiyEQv9s-UOaHx-a6-mF4Q
        if (code === "PNH" && type === "CITY") {
          return `KTI${LOCATION_IATA_CODE_AND_TYPE_SEPARATOR}AIRPORT`;
        }
      }
      return [code, type].filter(Boolean).join(LOCATION_IATA_CODE_AND_TYPE_SEPARATOR);
    })
    .join(PARAM_LIST_SEPARATOR);
}

/* start - flights_split_bounds_us */
type BoundsParams = { outboundOnly: true; inboundFor?: never } | { outboundOnly?: never; inboundFor: string };
/* end - flights_split_bounds_us */

function getSearchAPIParams(search: SearchState, query: string, bounds?: BoundsParams, isMobile?: boolean) {
  const params = new URLSearchParams(query);
  const { searchSegments } = search;

  // Add ?from=x&to=y parameters provided via URL path
  // They MUST override query parameters to ensure invalid routes like /FOO-BAR/ do not return results

  if (search.type === "MULTISTOP") {
    const fromCodes = searchSegments.map((eachSegment) => getLocationCodes(eachSegment.from));
    const toCodes = searchSegments.map((eachSegment) => getLocationCodes(eachSegment.to));
    params.set("from", fromCodes.join(MULTISTOP_IATA_LIST_SEPARATOR));
    params.set("to", toCodes.join(MULTISTOP_IATA_LIST_SEPARATOR));
  } else {
    //In case of oneway or roundtrip there will be only one segment with {from,to}
    const tripSegment = searchSegments[0];
    params.set("from", getLocationCodes(tripSegment.from));
    params.set("to", getLocationCodes(tripSegment.to));
  }

  const marketingParams = getSearchApiMarketingParams();
  for (const marketingParam in marketingParams) {
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    params.set(marketingParam, marketingParams[marketingParam]);
  }

  params.set("enableVI", "1");

  const isPinningMetaOfferOnSr = isMobile
    ? Boolean(trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_mdot", 1))
    : Boolean(trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_www", 1));

  if (isPinningMetaOfferOnSr && isOfMetaOrigin() && flightsSessionStore.get("landingOfferKey")) {
    params.set("offerKeyToHighlight", flightsSessionStore.get("landingOfferKey"));
  }

  // Removing baseOfferToken from url when selecting a different offer token from branded fares on search
  params.delete("baseOfferToken");
  params.delete("flexibleTicket");
  params.delete("cancelForAnyReason");
  params.delete("brandedFareIndex");

  // Handle split bounds
  if (bounds?.outboundOnly) {
    params.delete("inboundFor");
    params.set("outboundOnly", "1");
  }

  if (bounds?.inboundFor) {
    params.delete("outboundOnly");
    params.set("inboundFor", bounds.inboundFor);
  }

  return params;
}

export function useSearchAPIParams(bounds?: BoundsParams) {
  /* eslint-disable react-hooks/exhaustive-deps */
  /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
  /**
   * BEWARE: We intentionally omit the store search state from the dependency list.
   *
   * The way search currently works, is that we modify the state directly when operating
   * the inputs in the searchbox. The search is only performed when the URL changes, and the
   * URL change (history.push) is only applied on clicking the 'Search' button.
   *
   * tl;dr changing the store does NOT result in a new URL, or search results being reloaded
   * (but it should, if we want to do this the reactive way™)
   *
   * FIXME: have the searchbox hold its own state UNTIL THE USER applies the new search, and
   * only then we send it to our global store; alternatively have a `draftSearch`, the same
   * approach we used to solve this problem in search filters.
   *
   */
  // eslint-disable-next-line import/no-deprecated
  const { search } = useStore();
  const history = useHistory();
  const { isMobile } = useUserAgent();

  return useMemo(() => {
    const params = getSearchAPIParams(search, history.location.search, bounds, isMobile);

    return params;
  }, [history.location]);
}
